import { tracer } from "../datadog";
import { Survey, Block, Text, BooleanExpr, ClickQuery, Collection, ExpandedSurvey, Notification, Payment, PortalPageIcon, ProgramConfig, Section, Subsurvey, User, Users, SurveyComponent, NumberWithUnit, Root, SubsurveyRef, SubsurveyPathRef, TailwindColor, RichText, When, ConditionalReassign, NavigationOverrideNode, NavigationReferenceNode, SyntheticFolder, SupportedIcon } from "../survey";
import { getPaymentPreferencesTargetFields } from "./paymentPreferences";
export type { SupportedIcon };
import * as uuid from 'uuid';
import { nonNullable } from "../typechecks";
import { encodeWaypoint } from "./componentIdentifier";
const slugid = require('slugid');

type Field = string;
type Tag = string;
type Email = string;
type Channel = string;


export type BlockEdits = {
  block_when: BooleanExpr,
  blocked_message: RichText,
  block_mode?: 'full' | 'read_only',
  block_users_tagged?: string[]
}

export type ReviewConfiguration = {
  pinnedApplicantInfo: string[],
  showV2ToUsers?: string[],
  applicationState?: {
    field: string,
    colors: {
      stateValue: string,
      color: 'blue' | 'green' | 'red' | 'yellow'
    }[]
  },
  reassignReviewer?: {
    assigneeField: string | ConditionalReassign,
    limitAssigneeOptionsToTags?: string[],
  },
  includePayments?: boolean,
  expandApplicantInfoByDefault?: boolean,
  expandToolsPanelByDefault?: boolean,
}

export type NavigationNode = {
  /**
   * Stable identifier propagated from the underlying component's `id` field (Collections and
   * Sections currently). Used by Navigation overrides to target this node. Not set for
   * auto-derived nodes whose underlying component has no id.
   */
  id?: string,
  name: string | Text,
  href: string,
  icon?: SupportedIcon | any, // any because of heroicons
  subnodes?: NavigationNode[],
  isFolder?: boolean,
  limitedToApplicants?: BooleanExpr,
  shouldFastForward?: boolean | undefined,
  // Used for section identification for sections with the same title
  sectionPath?: string,
  defaultToClosed?: boolean,
  bold?: boolean,
  color?: TailwindColor,
  blockFurtherEdits?: BlockEdits,
  subsurveyTitle?: Text,
  reviewConfiguration?: ReviewConfiguration,
  kind?: 'Collection' | 'Section' | 'Subsurvey' | 'Dashboard' | 'Applicant Identities' | 'Applicant Portal Page' | 'Synthetic Folder',
  /**
   * Waypoint chain (see `encodeWaypoint`) locating this node's underlying component in the survey,
   * Only set for nodes backed by a real survey-tree component not under Templated Blocks.
   */
  tree?: string[],
  /**
   * Set when this node's underlying component came from inside a Templated Block.
   * Together with `_preSubstitutionIdentifier`, the node is tied back to
   * the un-expanded component the editor renders
   */
  _parentTemplatedBlockId?: string,
  /** The component's pre-substitution `getComponentIdentifier` value — see `_parentTemplatedBlockId`. */
  _preSubstitutionIdentifier?: string,
}

export function collectNavigation(props: Collection | Section | Subsurvey, scope?: "Global" | "Applicant", applicantUid?: string, path?: string, userTags?: string[], tree: string[] = []): NavigationNode | null {
  const appLinkPrefix = (applicantUid && applicantUid?.length > 22) ? '/ua/' : '/a/';

  let limitedToApplicants: BooleanExpr | undefined;
  if (props.kind === 'Collection') {
    limitedToApplicants = props.options?.limitedToApplicants;
  } else if (props.kind === 'Subsurvey') {
    const hfn = props.hideFromNavigation;
    if (!hfn) {
      // No restriction — always visible
    } else if (typeof hfn === 'boolean') {
      // Legacy boolean
      limitedToApplicants = hfn ? { kind: 'Never' as const } : { kind: 'Not' as const, clause: { kind: 'Never' as const } };
    } else if (hfn.kind === 'Conditional Hide From Navigation') {
      // New format
      limitedToApplicants = hfn.showWhen;
      // Filter by user tags if configured
      if (hfn.showForUsersTagged?.length && userTags) {
        const hasRequiredTag = hfn.showForUsersTagged.some(tag => userTags.includes(tag));
        if (!hasRequiredTag) return null;
      }
    }
  }
  const id = (props.kind === 'Collection' || props.kind === 'Section') && 'id' in props
    ? props.id
    : (props.kind === 'Subsurvey') 
      ? '/p/' + props.path
      : undefined;

  return {
    id: id,
    name: props.kind === 'Section' ? props.title : (props.kind === 'Subsurvey' ? '/p/' + props.path : props.name),
    href: props.kind === 'Section' ? appLinkPrefix + applicantUid + '#' + props.title.en : (props.kind === 'Subsurvey' ? appLinkPrefix + applicantUid + '/' + props.path : ''),
    sectionPath: props.kind === 'Section' ? path : '',
    _parentTemplatedBlockId: (props as any)._parentTemplatedBlockId,
    _preSubstitutionIdentifier: (props as any)._preSubstitutionIdentifier,
    icon: props.kind,
    isFolder: props.kind === 'Collection',
    limitedToApplicants,
    shouldFastForward: props.kind === 'Subsurvey' ? props.shouldFastForward : undefined,
    defaultToClosed: props.kind === 'Collection' ? props.options.defaultToClosed : undefined,
    bold: props.kind === 'Collection' ? props.options.bold : undefined,
    color: props.kind === 'Collection' ? props.options.color : undefined,
    blockFurtherEdits: props.kind === 'Subsurvey' ? props.blockFurtherEdits : undefined,
    subsurveyTitle: props.kind === 'Subsurvey' ? props.title : undefined,
    reviewConfiguration: props.kind === 'Subsurvey' ? props.review as ReviewConfiguration | undefined : undefined,
    kind: props.kind,
    tree,
    subnodes: (props.kind === 'Subsurvey' ? props.sections : props.components).map((c, i) => {
      const childTree = [...tree, encodeWaypoint(c, i)];

      // We no longer show subsurvey sections w/in the navigator since subsurveys
      // are progressively navigated
      if (c.kind === 'Subsurvey') {
        const childHfn = c.hideFromNavigation;
        if (scope === 'Global' || typeof childHfn === 'boolean' && childHfn) return null;
        return collectNavigation(c, scope, applicantUid, undefined, userTags, childTree);
      }

      if (c.kind === 'Collection') {
        return collectNavigation(c, scope, applicantUid, undefined, userTags, childTree);
      }

      if (c.kind === 'Section') {
        if (c.hidden || scope === "Global") {
          return null;
        }
        if (props.kind === 'Subsurvey') {
          // For sections inside subsurveys, need to also add in the path for uniqueness
          return collectNavigation(c, scope, applicantUid, props.path, userTags, childTree)
        }
        return collectNavigation(c, scope, applicantUid, undefined, userTags, childTree);
      }

      if (scope === 'Applicant' && c.kind === 'Applicant Identities') {
        return {
          name: c.title,
          href: '/ad/' + applicantUid + '/' + c.path,
          icon: 'Section',
          isFolder: false,
          kind: 'Applicant Identities',
          tree: childTree,
          _parentTemplatedBlockId: (c as any)._parentTemplatedBlockId,
          _preSubstitutionIdentifier: (c as any)._preSubstitutionIdentifier,
        } satisfies NavigationNode;
      }

      if (scope === 'Applicant' && c.kind === 'Applicant Portal Page') {
        return {
          name: c.title,
          href: '/ao/' + applicantUid + '/' + c.path,
          icon: c.icon || 'Dashboard',
          isFolder: false,
          kind: 'Applicant Portal Page',
          tree: childTree,
          _parentTemplatedBlockId: (c as any)._parentTemplatedBlockId,
          _preSubstitutionIdentifier: (c as any)._preSubstitutionIdentifier,
        } satisfies NavigationNode;
      }

      // The remaining pieces are all global scoped things
      if (scope === 'Applicant') return null;

      if (c.kind === 'Dashboard') {
        return {
          name: c.title,
          id: '/d/' + c.path,
          icon: 'Dashboard',
          href: '/d/' + c.path,
          kind: 'Dashboard',
          tree: childTree,
          _parentTemplatedBlockId: (c as any)._parentTemplatedBlockId,
          _preSubstitutionIdentifier: (c as any)._preSubstitutionIdentifier,
        } satisfies NavigationNode;
      }
      return null;

    }).filter(nonNullable),
  }
}

/**
 * Stable identifier for a nav node used by Navigation overrides. Prefers the component-derived
 * `id` (Collections/Sections with an id set), then falls back to the node's href, then to a
 * name-path slug. Returns null when no identifier can be derived (e.g. an unnamed folder with no id).
 */
export function navNodeIdentifier(node: NavigationNode, parentPath?: string): string | null {
  if (node.id) return node.id;
  if (node.href && node.href !== '#' && node.href !== '') return node.href;
  const displayName = typeof node.name === 'string' ? node.name : node.name?.en;
  if (!displayName) return null;
  const slug = displayName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return parentPath ? `${parentPath}/${slug}` : `name:${slug}`;
}


/**
 * Indexes every node in an auto-derived nav tree by its `navNodeIdentifier`, building parent
 * paths along the way so unnamed parents don't break lookups. Shared by `applyNavigationOverrides`
 * (to resolve `target` references) and by editor tooling that needs to resolve a `target` back to
 * the node it refers to (e.g. to show a friendly name next to an opaque id/href/name-path).
 */
export function indexNavNodesById(autoRoot: NavigationNode): Map<string, NavigationNode> {
  const autoById = new Map<string, NavigationNode>();
  function indexAuto(node: NavigationNode, parentPath?: string) {
    const id = navNodeIdentifier(node, parentPath);
    if (id) autoById.set(id, node);
    for (const c of node.subnodes ?? []) indexAuto(c, id ?? parentPath);
  }
  for (const top of autoRoot.subnodes ?? []) indexAuto(top);
  return autoById;
}

/**
 * Resolves the auto-derived navigation against a user-authored override tree. Semantics:
 *
 * - Override tree's top-level entries appear first in the resolved nav; any auto-derived top-level
 *   nodes not referenced anywhere in the override tree (and not explicitly `hidden`) fill in after.
 * - Within a parent whose children are explicitly overridden, override entries appear first; auto
 *   children not referenced elsewhere in the override tree fill in after.
 * - Within a parent that's referenced but whose children are NOT overridden, all auto children appear
 *   in natural order — minus any that were claimed by the override tree somewhere else.
 * - A target-referenced node with `hidden: true` is suppressed. Synthetic folders are honored as-is.
 * - Dead targets (no matching auto-node) are silently ignored.
 */
export function applyNavigationOverrides(autoRoot: NavigationNode, overrides?: NavigationOverrideNode[]): NavigationNode {
  if (!overrides?.length) return autoRoot;

  // 1. Index every auto-node by its identifier, building paths so unnamed parents don't break lookups.
  const autoById = indexNavNodesById(autoRoot);

  // 2. Collect every target referenced anywhere in the override tree, plus targets marked hidden.
  const claimed = new Set<string>();
  const hidden = new Set<string>();
  function collectClaims(nodes: NavigationOverrideNode[]) {
    for (const n of nodes) {
      const node = n.node;
      if (node.kind === 'Navigation Reference Node') {
        claimed.add(node.target);
        if (node.hidden) hidden.add(node.target);
      } else if (node.kind === 'Synthetic Folder') {
        collectClaims(node.children);
      }
    }
  }
  collectClaims(overrides);

  // Re-emit an auto subtree, dropping any descendants that were claimed by the override tree
  // (they'll appear at their override position instead).
  function pruneClaimed(node: NavigationNode, parentId?: string): NavigationNode {
    const nodeId = navNodeIdentifier(node, parentId) ?? parentId;
    const subnodes = (node.subnodes ?? [])
      .filter(c => {
        const id = navNodeIdentifier(c, nodeId);
        return !id || (!claimed.has(id) && !hidden.has(id));
      })
      .map(c => pruneClaimed(c, nodeId));
    return { ...node, subnodes };
  }

  function resolveOverride(node: NavigationReferenceNode | SyntheticFolder): NavigationNode | null {
    if (node.hidden) return null;

    if (node.kind === 'Navigation Reference Node') {
      const autoNode = autoById.get(node.target);
      if (!autoNode) return null;

      // Keep all of auto's children, except those claimed elsewhere.
      const parentId = node.target;
      const resolvedChildren = (autoNode.subnodes ?? [])
        .filter(c => {
          const id = navNodeIdentifier(c, parentId);
          return !id || (!claimed.has(id) && !hidden.has(id));
        })
        .map(c => pruneClaimed(c, parentId));

      return {
        ...autoNode,
        name: node.name || autoNode.name,
        icon: node.icon || autoNode.icon,
        color: node.color || autoNode.color,
        bold: node.bold ?? autoNode.bold,
        defaultToClosed: node.defaultToClosed ?? autoNode.defaultToClosed,
        subnodes: resolvedChildren,
      };
    }

    // Synthetic folder
    const resolvedChildren: NavigationNode[] = [];
    for (const c of node.children ?? []) {
      const r = resolveOverride(c.node);
      if (r) resolvedChildren.push(r);
    }
    return {
      name: node.name ?? { en: '(unnamed)' },
      href: '#',
      icon: node.icon || 'Collection',
      isFolder: true,
      color: node.color,
      bold: node.bold,
      defaultToClosed: node.defaultToClosed,
      subnodes: resolvedChildren,
      kind: 'Synthetic Folder',
    };
  }

  // 3. Build the resolved top-level: overridden entries first, then unclaimed auto top-level.
  const resolvedTop: NavigationNode[] = [];
  for (const o of overrides) {
    const r = resolveOverride(o.node);
    if (r) resolvedTop.push(r);
  }
  for (const autoTop of autoRoot.subnodes ?? []) {
    const id = navNodeIdentifier(autoTop);
    if (!id || (!claimed.has(id) && !hidden.has(id))) {
      resolvedTop.push(pruneClaimed(autoTop));
    }
  }

  return { ...autoRoot, subnodes: resolvedTop };
}

// True if `components` (a program's Collection/Dashboard tree) already defines a custom Dashboard
// at the given path — mirrors aidkit's `findDashboard`, reimplemented here (rather than imported)
// because `types` must not depend back on the `aidkit` backend package.
function hasDashboardAtPath(components: SurveyComponent[] | undefined, path: string): boolean {
  for (const c of components ?? []) {
    if (c.kind === 'Dashboard' && c.path === path) return true;
    if (c.kind === 'Collection' && hasDashboardAtPath(c.components, path)) return true;
  }
  return false;
}

/**
 * Sentinel for `roles` on the collect*Node functions below that bypasses their tag/role gating
 * entirely. Meant ONLY for the Distro editor's navigation preview, which has no real logged-in
 * user and wants to render every node that *could* exist as an override target under some role,
 * rather than guess a hardcoded superset of every gating tag (which silently drifts out of sync
 * as tags change, and was the actual bug this sentinel replaces).
 *
 * Safe by construction, not just by convention: it's a distinct string literal, never a `string[]`,
 * so it can't be produced by spreading/copying a real permissions array, and the one legitimate
 * source of `roles` on the backend (`token.perms` in `programNavigation`) is always a `string[]` —
 * there is no code path by which a request can cause this sentinel to be passed there instead.
 */
export const PREVIEW_ALL_ROLES = 'preview:all-roles' as const;
type RolesOrPreviewAll = string[] | typeof PREVIEW_ALL_ROLES;

function hasAnyTag(roles: RolesOrPreviewAll | undefined, tags: string[]): boolean {
  if (roles === PREVIEW_ALL_ROLES) return true;
  if (tags.length === 0) return true;
  return Array.isArray(roles) && tags.some(t => roles.includes(t));
}

// Program-level pages (User Management, Org Management, Payments, AI Chats, the default "All
// Applicants" dashboard, comms support dashboards) that live outside the survey's Collection/
// Dashboard tree but still show up in the sidebar and should be targetable by Navigation
// overrides.
//
// `href` for Payments/AI Chats is per-applicant, so those two carry a stable `id` (independent of
// `applicantUid`) for override targets to reference; `navNodeIdentifier` prefers `id` over `href`.

/** The default "All Applicants" dashboard — nests at the end of the Collections/Dashboards tree, not in the flat tail. */
export function collectAllApplicantsDashboardNode(params: {
  roles?: RolesOrPreviewAll,
  /** Entire program's component tree (unpruned), used only to skip this node when the survey defines its own dashboard at path "all". */
  components?: SurveyComponent[],
  /** Display name; i18n lives outside this package, so the caller supplies it. Defaults to the English string. */
  displayName?: string | Text,
}): NavigationNode | null {
  const { roles, components, displayName } = params;
  if (hasDashboardAtPath(components, 'all')) return null;
  if (!hasAnyTag(roles, ['admin', 'program screener', 'reviewer'])) return null;
  return {
    id: '/d/all',
    name: displayName ?? 'All Applicants',
    href: '/d/all',
    icon: 'Dashboard',
  };
}

export function collectUserManagementNode(params: { config: ProgramConfig, roles?: RolesOrPreviewAll }): NavigationNode | null {
  const { config, roles } = params;
  if (!hasAnyTag(roles, ['admin', 'user_manager'])) return null;
  if (!config.experimental?.userManagementConfig?.includeLinkInNav) return null;
  return { id: '/users', name: 'User Management', href: '/users', icon: 'Dashboard' };
}

export function collectOrgManagementNode(params: { config: ProgramConfig, roles?: RolesOrPreviewAll }): NavigationNode | null {
  const { config, roles } = params;
  if (!hasAnyTag(roles, ['admin'])) return null;
  if (!config.experimental?.includeLinkToOrgManagementPage) return null;
  return { id: '/orgs', name: 'Organization Management', href: '/orgs', icon: 'Dashboard' };
}

/** Renders ahead of the Collections/Dashboards tree, not in the flat tail — callers should splice it in at the top. */
export function collectPaymentsNode(params: { roles?: RolesOrPreviewAll, applicantUid?: string }): NavigationNode | null {
  const { roles, applicantUid } = params;
  if (!applicantUid) return null;
  if (!hasAnyTag(roles, ['admin', 'reviewer', 'program_admin', 'all-applicants.view_payments'])) return null;
  return { id: '/payments', name: 'Payments', href: '/payments/' + applicantUid, icon: 'Payment' };
}

export function collectAiChatsNode(params: { config: ProgramConfig, roles?: RolesOrPreviewAll, applicantUid?: string }): NavigationNode | null {
  const { config, roles, applicantUid } = params;
  if (!applicantUid) return null;
  if (!config.capabilities?.aiCoach || config.capabilities.aiCoach.disabled) return null;
  if (!hasAnyTag(roles, ['admin', 'ai_reviewer'])) return null;
  return { id: '/ai_chats', name: 'AI Chats', href: '/ai_chats/' + applicantUid, icon: 'AIChats' };
}

/**
 * Comms support dashboards, one per authorized channel. Only covers the modern `config.comms`
 * shape — the legacy Airtable-config fallback (`getChannelConfig(survey.configuration(), ...)`)
 * needs the backend Survey object and stays inline in `programNavigation`.
 */
export function collectSupportDashboardNodes(params: { config: ProgramConfig, roles?: RolesOrPreviewAll }): NavigationNode[] {
  const { config, roles } = params;
  const channels = [config.comms?.default, ...(config.comms?.otherChannels ?? [])].filter(nonNullable);
  const nodes: NavigationNode[] = [];
  for (const channel of channels) {
    if (!hasAnyTag(roles, channel.limitedToTags?.length ? channel.limitedToTags : ['comms', 'admin', 'admin-panel', 'support.view'])) continue;
    const configuredName = channel.supportDashboard?.name;
    const defaultName = `Applicant Support${channel.channelName === 'default' ? '' : ' (' + channel.channelName + ')'}`;
    nodes.push({
      id: '/supportrequestsv2/' + channel.channelName,
      name: configuredName?.en?.trim() ? configuredName : defaultName,
      href: '/supportrequestsv2/' + channel.channelName + '/all_unhandled',
      icon: 'SupportDashboard',
    });
  }
  return nodes;
}

export function collectApplicantNavigation(
  props: Collection | Section | Subsurvey,
  loggedIn: boolean,
  subnodes: NavigationNode[] = []
): NavigationNode {

  let limitedToApplicants: BooleanExpr | undefined;
  if (props.kind === 'Collection') {
    limitedToApplicants = props.options?.limitedToApplicants;
  } else if (props.kind === 'Subsurvey') {
    const hfn = props.hideFromNavigation;
    if (!hfn) {
      // No restriction — always visible
    } else if (typeof hfn === 'boolean') {
      // Legacy boolean
      limitedToApplicants = hfn ? { kind: 'Never' as const } : { kind: 'Not' as const, clause: { kind: 'Never' as const } };
    } else if (hfn.kind === 'Conditional Hide From Navigation') {
      // New format
      limitedToApplicants = hfn.showWhen;
    }
  }

  for (const c of (props.kind === 'Subsurvey' ? props.sections : props.components)) {
    if (c.kind === 'Applicant Portal Page' && (
      c.showWhen === 'Always' || c.showWhen === 'LoggedIn' && loggedIn || c.showWhen === 'LoggedOut' && !loggedIn
    )) {
      subnodes.push({
        name: c.title,
        href: '/o/' + c.path,
        icon: c.icon || 'Dashboard',
        isFolder: false,
      });
    }
    if (c.kind === 'Collection' || c.kind === 'Section') {
      if (c.kind === 'Section' && c.hidden) continue;
      collectApplicantNavigation(c, loggedIn, subnodes);
    }
  }

  return {
    name: props.kind === 'Section' ? props.title : (props.kind === 'Subsurvey' ? '/p/' + props.path : props.name),
    href: '',
    icon: props.kind,
    isFolder: props.kind === 'Collection',
    subnodes: subnodes,
    limitedToApplicants,
  }
}

export type PermissionScope = {
  applicantScope: BooleanExpr,
  tags: Tag[],
  channelScope?: Channel[],
  userId?: string,
} & FieldAndEditScope;

export type FieldAndEditScope = {
  fieldScope: Field[],
  copyEditScope: SubsurveyPathRef[],
}

export function EmailToUserId(email: string) {
  return slugid.encode(uuid.v5(email.trim().toLowerCase(), uuid.v5.URL));
}

export function collectScopeForTags(tags: Tag[], survey: Survey): FieldAndEditScope {
  const permissions = CollectFieldAndEditPermissions(survey);
  return {
    fieldScope: Object.keys(permissions.fieldScope).filter(field => {
      const fieldTags = permissions.fieldScope[field];
      return fieldTags.length === 0 || tags.some(tag => fieldTags.includes(tag));
    }),
    copyEditScope: Object.keys(permissions.copyEditScope).filter(subsurveyPath => {
      const subsurveyTags = permissions.copyEditScope[subsurveyPath];
      return subsurveyTags.length === 0 || tags.some(tag => subsurveyTags.includes(tag));
    }),
  }
}

export function CollectScopeForUser(user: string, survey: Survey, users?: Users): PermissionScope {
  const [usersTags, userFilters] = CollectUserTagsAndFilters(users || []);
  if (!usersTags[user]) {
    return {
      applicantScope: {
        kind: 'Never'
      },
      fieldScope: [],
      channelScope: [],
      copyEditScope: [],
      tags: []
    }
  }
  const tags = usersTags[user];
  const filters = userFilters[user];
  const permissions = CollectFieldAndEditPermissions(survey);

  return {
    applicantScope: {
      kind: 'And',
      clauses: filters
    },
    fieldScope: Object.keys(permissions.fieldScope).filter(field => {
      const fieldTags = permissions.fieldScope[field];
      return fieldTags.length === 0 || tags.some(tag => fieldTags.includes(tag));
    }),
    channelScope: [],
    tags: [],
    copyEditScope: Object.keys(permissions.copyEditScope).filter(subsurveyPath => {
      const subsurveyTags = permissions.copyEditScope[subsurveyPath];
      return subsurveyTags.length === 0 || tags.some(tag => subsurveyTags.includes(tag));
    }),
  }
}

export function CollectIndividualUsers(users: Users) {
  const toReturn: (User & { tags: Tag[], filters: BooleanExpr[] })[] = [];
  function traverse(users: Users, tags: Tag[], filters: BooleanExpr[]) {
    users.forEach(user => {
      if (user.kind === 'User') {
        toReturn.push({
          ...user,
          tags,
          filters
        })
      }
      if (user.kind === 'User Group') {
        traverse(user.members, 
          [...tags, ...user.tags], 
          [...filters, ...(user.limitedToApplicants ? [user.limitedToApplicants] : [])])
      }
    })
  };
  traverse(users, [], []);
  return toReturn;
}

export function CollectUserTagsAndFilters(users: Users): [Record<Email, Tag[]>, Record<Email, BooleanExpr[]>] {
  const userTags = {} as Record<Email, Tag[]>;
  const userFilters = {} as Record<Email, BooleanExpr[]>;
  function traverse(users: Users, tags: Tag[], filters: BooleanExpr[]) {
    users.forEach(user => {
      if (user.kind === 'User') {
        const uid = EmailToUserId(user.email);
        userTags[uid] = tags;
        userFilters[uid] = filters;
      }
      if (user.kind === 'User Group') {
        traverse(user.members, 
          [...tags, ...user.tags], 
          [...filters, ...(user.limitedToApplicants ? [user.limitedToApplicants] : [])])
      }
    })
  };
  traverse(users, [], []);
  return [userTags, userFilters];
}

export function CollectFieldAndEditPermissions(survey: Root): { fieldScope: Record<Field, Tag[]>, copyEditScope: Record<SubsurveyPathRef, Tag[]>, subsurveyScope: SubsurveyPathRef[] } {
  const fieldPermissions = {} as Record<Field, Tag[]>;
  const copyEditPermissions = {} as Record<SubsurveyPathRef, Tag[]>;

  const hasTagAlready = {} as Record<Field, Record<Tag, true | undefined>>;

  const addTagsToField = (targetField: Field, tags: Tag[]) => {
    // Don't add if field is empty
    if (!targetField) return;

    // If a field appears in a collection with no tags, 
    // intuit that means everyone should have access to it.
    // Overwrite existing tags, and given the if else below, 
    // it will always stay true the fieldPermissions[targetField] = [];
    if (tags.length === 0) {
      fieldPermissions[targetField] = tags;
      return;
    }

    hasTagAlready[targetField] ||= {};
        
    if (!fieldPermissions[targetField] || fieldPermissions[targetField]?.some(d => d)) {
      // If no tag array has been set or if specific tags have been defined,
      // Go ahead and push the rest.
      fieldPermissions[targetField] ||= [];

      for (const tag of tags) {
        if (!hasTagAlready[targetField][tag]) {
          fieldPermissions[targetField].push(tag);
          // Setting it in the record avoids tedious .includes calls
          hasTagAlready[targetField][tag] = true;
        }
      }
    } 

  }

  function traverse(block: SurveyComponent, tags: Tag[]) {
    if ('targetField' in block && block.targetField) {
      if ('allowedUserTags' in block) {
        tags = tags.concat(block.allowedUserTags);
      }
      addTagsToField(block.targetField, tags);
    }

    // Special case Likert because it is kind of a shell around its subquestions; it does
    // not have a top-level target field, but its subquestions do
    if (block.kind === 'Likert' && Array.isArray(block.questions)) {
      block.questions.forEach(question => {
        addTagsToField(question.targetField, tags);
      })
    }

    // Special case NumberWithUnit because it does not have a top-level target field
    if (block.kind === 'NumberWithUnit') {
      addTagsToField(block.numberQuestion.targetField, tags);
    }

    if (block.kind === 'Payment Preferences') {
      getPaymentPreferencesTargetFields(block).forEach(field => addTagsToField(field, tags));
    }

    // This will only be present in raw/non-expanded surveys
    if (block.kind == 'Templated Block') {
      block.components.flatMap((s) => traverse(s, tags));
    }

    if (block.kind == 'Subsurvey') {
      copyEditPermissions[block.path] = block?.copyEditing?.allowedUserTags ?? [];
      block.sections.flatMap((s) => traverse(s, tags));
    }

    if (block.kind == 'Section') {
      tags = tags.concat(block.limitedToTags ?? []);
      block.components.flatMap((s) => traverse(s, tags));
    }

    if (block.kind == 'Collection') {
      tags = tags.concat(block.options.limitedToTags ?? []);
      block.components.flatMap((s) => traverse(s, tags));
    }

    if (block.kind == 'Conditional Block') {
      block.components.flatMap((s) => traverse(s, tags));
      (block.otherwise || []).flatMap((s) => traverse(s, tags));
    }

    if (block.kind === 'Notification' || block.kind === 'InlineNotification') {
      addTagsToField(block.targetPrefix, tags);
    }

    if (block.kind === 'Payment' || block.kind === 'GiveCard Mailing' || block.kind === 'USBank Card Mailing') {
      addTagsToField(block.targetField, tags);
    }
  }
  ((Array.isArray(survey) ? survey : survey.survey) || []).flatMap((s) => traverse(s, []));

  return { fieldScope: fieldPermissions, copyEditScope: copyEditPermissions, subsurveyScope: Object.keys(copyEditPermissions) };
}

/**
 * Given a survey, creates a new survey that only contains the
 * components that are accessible to a user with the given tags.
 * Does not mutate the original survey, but the result will share
 * many object references.
 * 
 * @param survey
 * @param tags user tags for checking against limitedToTags
 * @returns pruned version of the same survey
 */
export const pruneForUserTags = (survey: Survey, tags: Tag[]): Survey => {
  return survey.reduce((prev, s) => {
    if (s.kind === 'Collection') {
      if (s.options.limitedToTags?.length && 
                !s.options.limitedToTags.some((t) => tags.includes(t))) {
        // console.log("PRUNING - Collection", s.options.limitedToTags, tags, s.name)
        return prev;
      }
      s = { ...s, components: pruneForUserTags(s.components, tags) };
    }
    if (s.kind === 'Subsurvey') {
      s = { ...s, sections: pruneForUserTags(s.sections, tags) as Subsurvey['sections'] };
    }
    if (s.kind === 'Section') {
      if (s.limitedToTags?.length && !s.limitedToTags.some((t) => tags.includes(t))) {
        // console.log("PRUNING - Section", s.limitedToTags, tags, s.title);
        return prev;
      }
    }
    prev.push(s);
    return prev;
  }, [] as Survey)
};


/*
export function FilterSurveyForUser(user: string, survey: ExpandedSurvey): ExpandedSurvey {
}
*/
